import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  PaperProps,
  Slider,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useStyles } from "../../style";
import HistogramFilterComponent from "../histogramFilterComponent";
import HistogramNew from "shared-component/newhistogram";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from "@mui/icons-material/Close";
import { histogramColorList } from "utility/constants";
import { getAggreList, getQuadPlotData } from "utility/helper";
import HistogramIcon from "assets/histogramIcon";

interface HistogramVisualizationPropTypes {
  data: any;
  signalListData: any;
  setAggregateList: (value: any) => void;
}

const minDistance = 5;

const DoublePlotHistogramVisualization = (props: HistogramVisualizationPropTypes) => {
  const { data, signalListData } = props;
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [filterIndex, setFilterIndex] = useState<number>(0);
  const [initialDomain, setInitialDomain] = useState<any[]>([]);

  const generateInitialBucketSize = (list: any, defaultValue = 5) => {
    let bucketSize: any = [];
    list?.forEach(() => {
      bucketSize.push(defaultValue)
    })
    return bucketSize
  }

  const generateInitialDomain = () => {
    let domain: any = [];
    data?.forEach((item: any) => {
      let numbersAsIntegers: any[] = [];
      item?.forEach((num: any) => {
        numbersAsIntegers.push(Math.round(parseInt(num)))
      });
      const highest = Math.max(...numbersAsIntegers);
      const lowest = Math.min(...numbersAsIntegers);
      domain.push([lowest, highest])
    })
    return domain
  }

  const initialSlicerDomain = generateInitialDomain();

  const initialBucketSize: any = generateInitialBucketSize(signalListData)
  const generateInitialTitle = (list: any) => {
    let title: any = [];
    list?.forEach(() => {
      title.push('Normal')
    })
    return title
  }
  const initialTitle: any = generateInitialTitle(signalListData)
  const generateHistoNorm = (list: any) => {
    let histonorm: any = [];
    list?.forEach(() => {
      histonorm.push('probability')
    })
    return histonorm
  }
  const histoNorm: any = generateHistoNorm(signalListData)
  const generateYAxisMaxValue = (list: any) => {
    let YAxisMaxValue: any = [];
    list?.forEach((item: any, index: number) => {
      if(index % 2 === 0) {
      YAxisMaxValue.push(null)
      }
    })
    return YAxisMaxValue
  }
  const initialYAxisMaxValue: any = generateYAxisMaxValue(signalListData)
  const generateYAxisMajorInterval = (list: any) => {
    let YAxisMajorInterval: any = [];
    list?.forEach(() => {
      YAxisMajorInterval.push(null)
    })
    return YAxisMajorInterval
  }

  const generateYAxisMinorInterval = (list: any) => {
    let YAxisMinorInterval: any = [];
    list?.forEach(() => {
      YAxisMinorInterval.push(null)
    })
    return YAxisMinorInterval
  }
  const initialYAxisMajorIntervalValue: any = generateYAxisMajorInterval(signalListData);
  const initialYAxisMinorIntervalValue: any = generateYAxisMinorInterval(signalListData)

  const style = useStyles();
  const cDadDataVisualizationData: any = {}
  const [histoData, setHistoData] = useState<any>([]);

  const generateDataAndLayout = async (newDomain: any = initialDomain) => {
    const list: any = [];
    const updatedQuadList = getQuadPlotData(data, 2);
    updatedQuadList?.forEach((dataElement: number[], parentIndex: number) => {
    const dataList: any = [];
    dataElement?.forEach((item: any, index: number) => {
      const listDomain = newDomain[index];
      if (listDomain?.length > 0) {
        const data: any = {
          x: item,
          type: 'histogram',
          histnorm: histoNorm[index],
          xbins: {
            start: listDomain[0],
            end: listDomain[1],
            size: initialBucketSize[index]
          },
          name: signalListData[index],
          marker: {
            color: histogramColorList[index].item,
            line: {
              color: histogramColorList[index].itemB,
              width: 1
            }
          }
        };
        if (index)
          if (index % 2 === 1) {
            data.opacity = 0.6;
          }
        dataList.push(data)
      }
    })

    const layout = {
      bargap: 0.05,
      bargroupgap: 0.2,
      barmode: "overlay",
      xaxis: { title: signalListData[0] },
      yaxis: {
        title: initialTitle[0],
        range: [0, initialYAxisMaxValue[parentIndex]],
        rangemode: 'nonnegative',
        dtick: initialYAxisMajorIntervalValue[parentIndex],
        minor: {
          dtick: initialYAxisMinorIntervalValue[parentIndex],
        },
      },
      modeBar: ''
    }
    list.push({ dataList, layout })
  });
    setHistoData(list)
  }

  useEffect(() => {
    const domainValue = generateInitialDomain();
    setInitialDomain(domainValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, signalListData])

  useEffect(() => {
    if (initialDomain?.length > 0) {
      generateDataAndLayout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDomain])

  function PaperComponent(props: PaperProps) {
    return (
      <Paper {...props} />
    );
  }

  const updateFilterData = (value: any) => {
    const { bucketSize, index, yAxisMajorInterval, yAxisMinorInterval, yAxisMaxValue, yAxisUnitValue } = value;
    initialBucketSize[index] = bucketSize;
    if (yAxisUnitValue?.length > 0) {
      initialTitle[index] = yAxisUnitValue;
      if (yAxisUnitValue.includes('Normalized')) {
        histoNorm[index] = 'probability';
        if (yAxisMajorInterval < initialYAxisMaxValue[index] || yAxisMajorInterval < 1) {
          initialYAxisMajorIntervalValue[index] = yAxisMajorInterval
        }
        if (yAxisMinorInterval < initialYAxisMaxValue[index] || yAxisMinorInterval < 1) {
          initialYAxisMinorIntervalValue[index] = yAxisMinorInterval
        }
      } else {
        histoNorm[index] = '';
      }
    }
    (yAxisUnitValue !== 'Normalized' && yAxisMaxValue > 1) ? initialYAxisMaxValue[index] = yAxisMaxValue : initialYAxisMaxValue[index] = null;
    generateDataAndLayout();
    if (modalOpen) setModalOpen(false);
  }

  const handleSlicerChange = async (
    newValue: number | number[],
    activeThumb: number,
    index: any
  ) => {
    let updateDomainValue: number[];
    if (!Array.isArray(newValue)) {
      return;
    }

    if (newValue[1] - newValue[0] < minDistance) {
      if (activeThumb === 0) {
        const clamped = Math.min(newValue[0], 100 - minDistance);
        updateDomainValue = [clamped, clamped + minDistance];
      } else {
        const clamped = Math.max(newValue[1], minDistance);
        updateDomainValue = [clamped - minDistance, clamped];
      }
    } else {
      updateDomainValue = newValue as number[];
    }
    const newDomain = initialDomain;
    newDomain[index] = updateDomainValue;
    setInitialDomain(newDomain);
    generateDataAndLayout(newDomain);
  };

  const getMarks = (min: any, max: any) => {

    const marks = [
      {
        value: min,
        label: min,
      },
      {
        value: max,
        label: max,
      },
    ];
    return marks;
  }

  useEffect(() => {
    let axisData: string[] = [];
    if (cDadDataVisualizationData?.data) {
      for (const [key] of Object.entries<string>(cDadDataVisualizationData?.data)) {
        axisData.push(key);
      };
    }
    props.setAggregateList(getAggreList(axisData))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cDadDataVisualizationData?.data])

  return (
    <Box>
      {cDadDataVisualizationData?.isLoading ?
        <div className={style.overlay}>
          <div className={style.loader}></div>
        </div> :
        data ? <>
          {signalListData?.length <= 2 ? <div
            style={{
              display: 'flex',
            }}
          ><Paper
            elevation={2}
            sx={{
              margin: "1%",
              padding: '2%',
              width: '75%'
            }}
          >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  width: '100%',
                  height: '100%'
                }}
              >
                <HistogramFilterComponent updateFilterData={updateFilterData} index={filterIndex} />
              </div>

            </Paper>
            <Paper
              elevation={2}
              sx={{
                margin: "1%",
                padding: '2%',
                width: '100%',
              }}
            >
              {data && histoData?.length > 0 && initialDomain?.length > 0 && initialDomain[0]?.length > 0 ? <div>
                <HistogramNew data={[histoData[filterIndex]?.dataList]} layout={histoData[filterIndex]?.layout} />
                <div style={{ textAlign: 'center' }}><Slider style={{ width: '50%' }}
                 onChange={(event: Event,
                  newValue: number | number[],
                  activeThumb: number) => handleSlicerChange(newValue, activeThumb, filterIndex)}
                aria-label="Default"
                valueLabelDisplay="auto"
                defaultValue={[initialDomain[0][0], initialDomain[0][1]]}
              min={initialSlicerDomain[0][0]}
              max={initialSlicerDomain[0][1]}
              marks={getMarks(initialSlicerDomain[0][0], initialSlicerDomain[0][1])} /></div>
              </div> : null}
              {!data &&
                <Box
                  flex={1}
                  my={2}
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"center"}
                >
                  <HistogramIcon />
                  <Typography
                    variant="caption"
                    textAlign={"center"}
                    color={theme.palette.text.disabled}
                    fontWeight={"bold"}
                    width={350}
                  >
                    Use the  {data ? 'Aggregation above' : 'Signal/Channel'} from dropdowns to analyse in Heat Map data chart!
                  </Typography>
                </Box>
              }
            </Paper>
          </div>
            :
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: '1rem',
              columnGap: '1rem',
              padding: '1rem'
            }}>
              {histoData?.length >= 1 && histoData?.map((item: any, index: number) => {
                return (
                  <Paper
                    elevation={2}
                    sx={{
                      width: '100%',
                    }}
                  >
                    <div>
                      {<IconButton
                        color="error"
                        onClick={(e) => {
                          setFilterIndex(index)
                          setModalOpen(true);
                        }}
                        edge="end"
                        size="small"
                        style={{ justifyContent: 'flex-end' }}
                      >
                        <MoreVertIcon />
                      </IconButton>}
                      {data && histoData && initialDomain?.length > 0 && initialDomain[index]?.length > 0 && histoData[index]?.dataList?.length > 0 && <div>
                        <HistogramNew data={[histoData[index]?.dataList]} layout={histoData[index]?.layout} />
                        <div style={{ textAlign: 'center' }}><Slider style={{ width: '50%' }} 
                        aria-label="Default" 
                        valueLabelDisplay="auto" 
                        onChange={(event: Event,
                          newValue: number | number[],
                          activeThumb: number) => handleSlicerChange(newValue, activeThumb, index)}
                        min={initialSlicerDomain[index][0]}
                        max={initialSlicerDomain[index][1]}
                        defaultValue={[initialDomain[index][0], initialDomain[index][1]]}
                        marks={getMarks(initialSlicerDomain[index][0], initialSlicerDomain[index][1])}
                     
                        /></div>
                      </div>}
                    </div>
                  </Paper>)
              })}
            </div>}
          <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            PaperComponent={PaperComponent}
          >
            <IconButton
              aria-label="close"
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
              onClick={() => setModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent sx={{ width: 400, height: 500 }} >
              <HistogramFilterComponent updateFilterData={updateFilterData} index={filterIndex} />
            </DialogContent>
          </Dialog>
        </>
          :
          <>
            <Box
              flex={1}
              my={2}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
            >
              <HistogramIcon />
              <Typography
                variant="caption"
                textAlign={"center"}
                color={theme.palette.text.disabled}
                fontWeight={"bold"}
                width={350}
              >
                Use the  {data ? 'Aggregation above' : 'Signal/Channel'} from dropdowns to analyse in Heat Map data chart!
              </Typography>
            </Box>
          </>
      }
    </Box>
  );
};

export default DoublePlotHistogramVisualization;  